/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { ISchemaConstArrayModel, SCHEMA_COMMENT_STATUS, TSchemaComment } from "@adapptio/lib-blueprint";
import { FieldRow } from "../../../ui/components/Fields/FieldRow";
import { FieldSwitch } from "../../../ui/components/Fields/FieldSwitch";
import { FieldLabel } from "../../../ui/components/Fields/FieldLabel";
import { InspectorControlCommentsListItem } from "./InspectorControlCommentsListItem";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";

export interface IInspectorControlCommentsListProps {
	model: ISchemaConstArrayModel<TSchemaComment>;
	onAcceptMessage: (index: number) => void;
	onDeclineMessage: (index: number) => void;
	onRemoveMessage: (index: number) => void;
	onUnresolveMessage: (index: number) => void;
}

export const InspectorControlCommentsList: React.FC<IInspectorControlCommentsListProps> = ({
	model,
	onAcceptMessage,
	onDeclineMessage,
	onRemoveMessage,
	onUnresolveMessage
}) => {
	useBlueprintModel(model);
	const baseClass = BEM("edt-inspector-control-comments-list");

	const [ showResolved, setShowResolved ] = useState(false);

	if (!model?.items?.length) {
		return null;
	}

	const items = showResolved
		? [ ...model.items ]
		: [ ...model.items ].filter((item) => item.props.status.value === SCHEMA_COMMENT_STATUS.UNRESOLVED);

	return (
		<div className={baseClass.toClassName()}>
			<FieldRow className={baseClass.element("filter").toClassName()}>
				<FieldSwitch value={showResolved} onChange={setShowResolved} />
				<FieldLabel>Show resolved comments</FieldLabel>
			</FieldRow>
			{items
				.map((item, index) => {
					return (
						<InspectorControlCommentsListItem
							key={item.nodeId}
							model={item}
							index={index}
							onAccept={onAcceptMessage}
							onDecline={onDeclineMessage}
							onRemove={onRemoveMessage}
							onReset={onUnresolveMessage}
						/>
					);
				})
				.reverse()}
		</div>
	);
};
